import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import client from './client'

export function useComments(mangaId, page = 1, sort = 'new') {
  return useQuery({
    queryKey: ['comments', mangaId, page, sort],
    queryFn: () => client.get(`/api/manga/${mangaId}/comments`, { params: { page, limit: 12, sort } }).then((r) => r.data),
    enabled: !!mangaId,
    placeholderData: keepPreviousData,
    refetchInterval: 25000,
  })
}

export function useChapterComments(mangaSlug, chapterId) {
  return useInfiniteQuery({
    queryKey: ['comments', mangaSlug, 'chapter', chapterId],
    queryFn: ({ pageParam = 1 }) =>
      client.get(`/api/manga/${mangaSlug}/comments`, {
        params: { chapter_id: chapterId, page: pageParam, limit: 10, sort: 'new' },
      }).then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (last) => last.page < last.pages ? last.page + 1 : undefined,
    enabled: !!(mangaSlug && chapterId),
    refetchInterval: 25000,
  })
}

export function useAddComment(mangaId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ text, parent_id = null }) =>
      client.post(`/api/manga/${mangaId}/comments`, { text, parent_id }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', mangaId] }),
  })
}

export function useAddChapterComment(mangaSlug, chapterId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ text, parent_id = null }) =>
      client.post(`/api/manga/${mangaSlug}/comments`, { text, parent_id, chapter_id: chapterId }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', mangaSlug, 'chapter', chapterId] }),
  })
}

export function useDeleteComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => client.delete(`/api/comments/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments'] }),
  })
}

export function useReportComment() {
  return useMutation({
    mutationFn: ({ commentId, reason }) =>
      client.post(`/api/comments/${commentId}/report`, { reason }).then((r) => r.data),
  })
}

export function useToggleReaction(mangaId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, type }) =>
      client.post(`/api/comments/${commentId}/reaction`, { type }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comments'] })
    },
  })
}
